/**
 * uiSwitch — shared markup for the wired on/off switch and the settings row
 * that carries it.
 *
 * Backed by css/components/switch.css. Used by /partner's sharing card and
 * /account-settings' notification rows, which is why the row wrapper speaks
 * the `.account-row` vocabulary (account-page.css) that both pages already use.
 *
 * The control is a `role="switch"` button rather than a checkbox: pages own
 * the persistence, and every consumer re-renders from state after a save.
 */

import { escapeHtml } from '../core/utils.js';

/**
 * The switch itself.
 *
 * @param {Object} options
 * @param {boolean} options.on - Current state; drives the class and aria-checked
 * @param {boolean} [options.disabled=false] - Render non-interactive (e.g. save in flight)
 * @param {string} options.action - data-action value the host section dispatches on
 * @param {string} [options.dim] - Optional data-dim payload (per-area rows)
 * @param {boolean} [options.small=false] - Subordinate `--sm` size
 * @param {string} [options.ariaLabel] - Accessible name, when no label element exists
 * @param {string} [options.ariaLabelledby] - Id of the label element naming this switch
 * @returns {string} HTML string
 */
export function renderSwitch({ on, disabled = false, action, dim, small = false, ariaLabel, ariaLabelledby }) {
  const labelAttr = ariaLabelledby
    ? `aria-labelledby="${ariaLabelledby}"`
    : `aria-label="${escapeHtml(ariaLabel || 'Toggle')}"`;
  return `
    <button type="button"
            class="switch ${on ? 'switch--on' : 'switch--off'}${small ? ' switch--sm' : ''}"
            role="switch"
            aria-checked="${on ? 'true' : 'false'}"
            ${labelAttr}
            data-action="${action}"
            ${dim ? `data-dim="${escapeHtml(dim)}"` : ''}
            ${disabled ? 'disabled' : ''}>
      <span class="switch__thumb" aria-hidden="true"></span>
    </button>
  `;
}

/**
 * One settings row: label + hint on the left, the switch on the right.
 *
 * Anything nested in `__body` is width-capped by the switch beside it, so
 * full-width state notes belong as siblings of the returned row, not inside it.
 *
 * @param {Object} options
 * @param {string} options.label - Short row title (HTML-safe, may contain markup)
 * @param {string} options.hint - Sentence explaining what the switch governs
 * @param {string} options.id - Id given to the label, used as the switch's aria-labelledby
 * @param {boolean} options.on - Current state
 * @param {boolean} [options.disabled] - Render non-interactive
 * @param {string} options.action - data-action value the host section dispatches on
 * @returns {string} HTML string
 */
export function renderSwitchRow({ label, hint, id, on, disabled, action }) {
  return `
    <div class="account-row">
      <div class="account-row__body">
        <span class="account-row__label" id="${id}">${label}</span>
        <span class="account-row__value account-row__value--wrap">${hint}</span>
      </div>
      <div class="account-row__action">
        ${renderSwitch({ on, disabled, action, ariaLabelledby: id })}
      </div>
    </div>
  `;
}
